import { ref, Ref } from "vue";

import { addBreadcrumb, captureMessage } from "@sentry/browser";
import type { Breadcrumb } from "@sentry/browser";

import MediaPlayer, {
  MediaPlayerCommand,
  MediaPlayerState,
} from "../media/player";
import { getDebugMode } from "@/helpers/helperFunctions";
import type { DoNotReportError } from "@/helpers/sentry";
import { assertNever } from "../../../modules/shared-utils/assertions";

type CaptureContext = Parameters<typeof captureMessage>[1];

export const EMPTY_PAGE_DURATION = 600;

export enum UtteranceKind {
  Text,
  // used for example for empty pages
  Pause,
}

export type UtteranceConfigurer = (utterance: SpeechSynthesisUtterance) => void;

type HighlightCallback = (
  word: string,
  start: number,
  length: number
) => void | Promise<void>;

type BaseUtterance = {
  previous: () => Promise<Utterance | null>;
  next: () => Promise<Utterance | null>;
  highlight: HighlightCallback;
};

type TextUtterance = BaseUtterance & {
  kind: UtteranceKind.Text;
  text: string;
  configure: UtteranceConfigurer;
};

type PauseUtterance = BaseUtterance & {
  kind: UtteranceKind.Pause;
  duration: number; // in milliseconds
};

export type Utterance = TextUtterance | PauseUtterance;

export type Sentence = {
  text: string;
  configure: UtteranceConfigurer;
  highlight: HighlightCallback;
};

export type ActiveSentence = {
  playing: Ref<boolean>;
  cancel: () => void;
};

export class TTSError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TTSError";
  }
}

export class TTSCancelledError extends TTSError implements DoNotReportError {
  public readonly doNotReportError: true = true;

  constructor() {
    super("TTS operation was cancelled");
    this.name = "TTSCancelledError";
  }
}

class TTSTimeoutError extends TTSError {
  constructor(operation: string, timeout: number) {
    super(`TTS operation ${operation} timed out after ${timeout} ms`);
    this.name = "TTSTimeoutError";
  }
}

type TTSEvent = "lastElementFound";

export const TTSHighlightUtterance = "tts-utterance";
export const TTSHighlightWord = "tts-word";

function promiseUtteranceStart(
  utterance: SpeechSynthesisUtterance,
  label: string,
  timeout: number,
  isCancelling: () => boolean
): Promise<void> {
  const startedAt = Date.now();
  const { promise, resolve, reject } = Promise.withResolvers<void>();

  const onStart = () => complete() && resolve();
  utterance.addEventListener("start", onStart);

  const fail = (err: Error) => complete() && reject(err);
  const onError = (ev: SpeechSynthesisErrorEvent) =>
    fail(new TTSError(`Error ${label} TTS: ${ev.error}`));
  utterance.addEventListener("error", onError);

  const onCancel = () => isCancelling() && fail(new TTSCancelledError());
  utterance.addEventListener("cancelled", onCancel);

  const timeoutHandle = setTimeout(
    () => fail(new TTSTimeoutError(label, timeout)),
    timeout
  );

  let done: boolean = false;
  function complete() {
    if (done) return false;
    done = true;

    utterance.removeEventListener("start", onStart);
    utterance.removeEventListener("error", onError);
    utterance.removeEventListener("cancelled", onCancel);
    clearTimeout(timeoutHandle);

    // TTSEngine.cancel() was called,
    // we must reject the promise to avoid leaving it hanging
    // but to not report it, we change the error type to TTSCancelledError
    if (isCancelling()) {
      reject(new TTSCancelledError());
      return false;
    }

    return true;
  }

  return promise;
}

// ChromeOS is extremely slow to start TTS,
// so we need to increase the timeout significantly when running on that platform
// const isChromeOS =
//   (navigator as any).userAgentData?.platform === "Chrome OS" ||
//   navigator.userAgent.includes("CrOS");
const MINUTE_MS = 60_000;
const PRIMING_TIMEOUT = 2 * MINUTE_MS; // isChromeOS ? 1 * MINUTE_MS : 20_000;
const SPEAK_TIMEOUT = 3 * MINUTE_MS; //isChromeOS ? 3 * MINUTE_MS : 45_000;

class TTSEngine extends EventTarget implements MediaPlayer {
  private activeUtterance: Utterance | null = null;
  private activePauseTimeout: ReturnType<typeof setTimeout> | null = null;
  private activeSentence: ActiveSentence | null = null;
  private _state: Ref<MediaPlayerState> = ref(MediaPlayerState.Stopped);

  private _ttsPrimed: boolean = false;
  private _ttsPriming: { promise: Promise<void>; cancelled: boolean } | null =
    null;

  private _cancelling: boolean = false;

  // we need to keep references to utterances to prevent them from being GC'd
  private readonly _utteranceRefs = new Set<SpeechSynthesisUtterance>();
  private callSpeak(utterance: SpeechSynthesisUtterance): void {
    const cleanup = () => {
      this._utteranceRefs.delete(utterance);
      utterance.removeEventListener("end", cleanup);
      utterance.removeEventListener("error", cleanup);
    };
    this._utteranceRefs.add(utterance);
    utterance.addEventListener("end", cleanup);
    utterance.addEventListener("error", cleanup);

    this.api.speak(utterance);
  }

  constructor(private readonly api: SpeechSynthesis) {
    super();
  }

  /**
   * iOS requires a user gesture to start the TTS engine,
   * so we can play an empty utterance to prime it
   * before doing any async/await operations that would lose the user gesture context
   */
  async prime(): Promise<void> {
    if (this._ttsPriming) {
      const priming = this._ttsPriming;
      await priming.promise;
      if (priming.cancelled) {
        throw new TTSCancelledError();
      }
    }
    if (this._ttsPrimed) {
      return;
    }

    const voices = this.api.getVoices();
    const data: Breadcrumb["data"] = {
      voicesCount: voices.length,
      voices: voices
        .map(
          (v) =>
            `${v.name}(${v.lang})${v.default ? "[default]" : ""}${v.localService ? "[local]" : ""}`
        )
        .slice(0, 5)
        .join(", "),
    };

    /**
     * window.event is deprecated but still a good heuristic
     * for checking if we're in a user gesture context / call stack
     */
    if (!window.event) {
      addBreadcrumb({
        category: "tts",
        message: "Attempting to prime TTS engine without user gesture",
        level: "warning",
        data,
      });
    } else {
      addBreadcrumb({
        category: "tts",
        message: "Priming TTS engine",
        level: "info",
        data: {
          eventType: window.event.type,
          ...data,
        },
      });
    }

    // ChromeOS requires a non-empty utterance to start successfully,
    // otherwise it won't ever start and won't provide any error
    const primingUtterance = new SpeechSynthesisUtterance(" ");
    const priming = (this._ttsPriming = {
      promise: this.speakWithTimeout(
        primingUtterance,
        "priming",
        PRIMING_TIMEOUT,
        // we don't actually cancel priming
        // we've seen it being cancelled due to user impatience
        // while the timing was within observed delays
        // it's best to let the process complete
        () => false
      ),
      cancelled: false,
    });

    try {
      await priming.promise;
    } finally {
      this._ttsPriming = null;
    }

    if (priming.cancelled) {
      throw new TTSCancelledError();
    }

    this._ttsPrimed = true;
  }

  public get state(): MediaPlayerState {
    return this._state.value;
  }

  private set state(value: MediaPlayerState) {
    this._state.value = value;
  }

  private emit(event: TTSEvent) {
    this.dispatchEvent(new CustomEvent(event));
  }

  public async playSentence(sentence: Sentence): Promise<ActiveSentence> {
    if (!this.api) {
      return null;
    }

    const clear = (active: ActiveSentence) => {
      active.playing.value = false;
      if (this.activeSentence !== active) {
        addBreadcrumb({
          category: "tts",
          message: "Cleared sentence doesn't match active sentence",
          level: "warning",
        });
      } else {
        this.activeSentence = null;
      }
      this.cancel();
    };

    // reset the browser's TTS
    if (this.activeSentence) {
      clear(this.activeSentence);
    } else {
      this.cancel();
    }

    const playing = ref(false);
    const cancel = () => clear(currentSentence);
    const currentSentence: ActiveSentence = { playing, cancel };

    this.activeSentence = currentSentence;

    const utterance = new SpeechSynthesisUtterance(sentence.text);
    sentence.configure(utterance);

    // highlight at least the full sentence in case we don't get any boundaries from the TTS engine
    utterance.addEventListener("start", () => sentence.highlight(null, 0, 0));
    utterance.addEventListener("boundary", async (event) => {
      const { charIndex, charLength } = event;
      const text = utterance.text.slice(charIndex, charIndex + charLength);
      await sentence.highlight(text, charIndex, charLength);
    });
    utterance.addEventListener("error", cancel);
    utterance.addEventListener("end", cancel);

    try {
      await this.speakWithTimeout(utterance, "sentence", SPEAK_TIMEOUT);
      playing.value = true;
    } catch (err) {
      cancel();
      throw err;
    }

    return currentSentence;
  }

  public async start(ut: Utterance): Promise<void> {
    if (!this.api) {
      return;
    }
    this.cancel();

    if (ut.kind === UtteranceKind.Pause) {
      this.state = MediaPlayerState.Playing;
      return await this.handlePauseUtterance(ut);
    }

    const utterance = this.prepareNextUtterance(ut, false);
    await this.speakWithTimeout(utterance, "starting", SPEAK_TIMEOUT);
    this.state = MediaPlayerState.Playing;
  }

  private async speakWithTimeout(
    utterance: SpeechSynthesisUtterance,
    label: string,
    timeout: number,
    isCancelling: () => boolean = () => this._cancelling
  ): Promise<void> {
    const promise = promiseUtteranceStart(
      utterance,
      label,
      timeout,
      isCancelling
    );
    this.callSpeak(utterance);

    // here, we want to confirm browser behaviors in production
    // if our expectation ends up being correct,
    // we could eliminate the timeout and just rely
    // on the resulting `speaking` value
    const speakingOutcome = this.api.speaking;
    // FF will set pending instead
    const pendingOutcome = this.api.pending;

    const ctx: CaptureContext = {
      level: "warning",
      extra: {
        label,
        speakingOutcome,
        pendingOutcome,
        utterance: utterance.text,
        voice: utterance.voice?.name ?? "<no voice selected>",
      },
    };
    try {
      await promise;

      // on success, we expect speechSynthesis.speak() to set speaking to true synchronously
      if (!speakingOutcome && !pendingOutcome) {
        captureMessage(
          `Successful speechSynthesis.speak() did not set speaking/pending to true synchronously`,
          ctx
        );
      }
    } catch (err) {
      // callSpeak already manage GC references on error/end
      // but we also need to handle them for timeouts
      this._utteranceRefs.delete(utterance);

      // rapid interactions with the media player controls can cause
      // an utterance to be cancelled while we're still handling its start
      // ignore those errors as they are expected and not actionable
      if (err instanceof TTSCancelledError) {
        // still bubble the error up to stop the current operation,
        // TTSCancelledError isn't reported to Sentry
        throw err;
      }

      // on failure, we expect speechSynthesis.speak() to not have set speaking to true synchronously
      if (speakingOutcome || pendingOutcome) {
        captureMessage(
          `Failed speechSynthesis.speak() did set speaking/pending to true synchronously`,
          ctx
        );
      }

      throw err;
    }
  }

  public can(command: MediaPlayerCommand): boolean {
    if (!this.api) {
      return false;
    }

    switch (command) {
      case "pause":
        return this.state === MediaPlayerState.Playing;
      case "resume":
        return this.state === MediaPlayerState.Paused;
      case "stop":
        return this.state !== MediaPlayerState.Stopped;
      case "previous":
      case "next":
        // TODO: check if the iterator actually allows it?
        return (
          this.state !== MediaPlayerState.Stopped && !!this.activeUtterance
        );
      default:
        return assertNever(command);
    }
  }

  public pause(): void {
    if (!this.can("pause")) {
      return;
    }
    this.state = MediaPlayerState.Paused;
    this.api.pause();
  }

  public resume() {
    if (!this.can("resume")) {
      return;
    }
    // TODO: handle long pauses bug
    this.state = MediaPlayerState.Playing;
    this.api.resume();
  }

  public previous(): Promise<void> {
    return this.go("previous");
  }

  public next(): Promise<void> {
    return this.go("next");
  }

  private async go(direction: "previous" | "next") {
    if (!this.can(direction)) {
      return;
    }
    this.cancel();

    const utterance = await this.activeUtterance[direction]();
    if (utterance) {
      return await this.start(utterance);
    } else {
      this.activeUtterance = null;
      this.state = MediaPlayerState.Stopped;
    }
  }

  public stop() {
    this.state = MediaPlayerState.Stopped;
    this.cancel();
  }

  private prepareNextUtterance(
    ut: TextUtterance,
    speak: boolean = true
  ): SpeechSynthesisUtterance {
    const utterance = new SpeechSynthesisUtterance(ut.text);
    ut.configure(utterance);

    let nextUtterance: Utterance | null = null;
    let nextUtteranceEmpty: boolean = false;
    utterance.addEventListener("start", async () => {
      if (getDebugMode()) console.log("utterance started", utterance.text);
      this.activeUtterance = ut;
      nextUtteranceEmpty = false;

      // highlight at least the full utterance in case we don't get any boundaries from the TTS engine
      await ut.highlight(null, 0, 0);

      // TODO: be more smart about when we fetch the next utterance
      nextUtterance = await ut.next();
      if (nextUtterance && nextUtterance.kind === UtteranceKind.Text) {
        this.prepareNextUtterance(nextUtterance, true);
      } else {
        nextUtteranceEmpty = true;
      }
    });

    utterance.addEventListener("boundary", async (event) => {
      const { charIndex, charLength } = event;
      const text = utterance.text.slice(charIndex, charIndex + charLength);
      // console.log("utterance boundary", text, charIndex, charLength);
      await ut.highlight(text, charIndex, charLength);
    });

    utterance.addEventListener("pause", (event) => {
      console.log(
        "utterance paused",
        utterance.text,
        event.charIndex,
        event.charLength
      );
    });

    utterance.addEventListener("resume", (event) => {
      console.log(
        "utterance resumed",
        utterance.text,
        event.charIndex,
        event.charLength
      );
    });

    let interrupted = false;
    utterance.addEventListener("error", (ev) => {
      if (this._cancelling) return;

      if (ev.error === "interrupted") {
        interrupted = true;
        return;
      }
      console.error("utterance error", ev);
    });

    utterance.addEventListener("end", async () => {
      if (this._cancelling) return;
      if (interrupted) return;

      console.log("utterance ended", utterance.text);
      if (nextUtterance && nextUtterance.kind === UtteranceKind.Pause) {
        await this.handlePauseUtterance(nextUtterance);
      }
      if (nextUtteranceEmpty) {
        this.emit("lastElementFound");
      }
    });

    if (speak) {
      this.callSpeak(utterance);
    }

    return utterance;
  }

  private async handlePauseUtterance(ut: PauseUtterance): Promise<void> {
    this.activeUtterance = ut;

    await ut.highlight(null, 0, 0);

    console.log("pause utterance", `${ut.duration}ms`);
    const nextUtterance: Utterance | null = await ut.next();
    this.activePauseTimeout = setTimeout(async () => {
      this.activePauseTimeout = null;
      this.activeUtterance = null;
      switch (nextUtterance?.kind) {
        case UtteranceKind.Text:
          this.prepareNextUtterance(nextUtterance, true);
          break;
        case UtteranceKind.Pause:
          await this.handlePauseUtterance(nextUtterance);
          break;
        case null:
        case undefined:
          this.state = MediaPlayerState.Stopped;
          console.log("No more utterances to play", nextUtterance);
          break;
        default:
          this.state = MediaPlayerState.Stopped;
          this.cancel();
          assertNever(nextUtterance);
      }
    }, ut.duration);
  }

  private cancel() {
    try {
      this._cancelling = true;
      if (this.activePauseTimeout !== null) {
        clearTimeout(this.activePauseTimeout);
        this.activePauseTimeout = null;
      }
      this._utteranceRefs.forEach((u) =>
        u.dispatchEvent(new Event("cancelled"))
      );
      this._utteranceRefs.clear();
      if (!this._ttsPriming) {
        this.api?.cancel();
      } else if (!this._ttsPriming.cancelled) {
        this._ttsPriming.cancelled = true;

        // reset SpeechSynthesis api once after current priming attempt is finished
        this._ttsPriming.promise
          .finally(() => {
            this._ttsPriming = null;
            this.cancel();
          })
          // avoid unhandled rejection of the .finally promise
          .catch(() => {});
      }
    } finally {
      this._cancelling = false;
    }
  }
}

export default new TTSEngine(window.speechSynthesis);
